import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, KeyRound, ShieldCheck, X } from "lucide-react";
import { recoverCurrentIdentity } from "../lib/identity";
import { clearSigningCache } from "../lib/signedRequest";
import { clearDmKeyCache } from "../lib/dmCrypto";

function normalizeWords(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
}

export function IdentityRecoveryDialog({ open, onClose, onRecovered, actionLabel = "Ripristina e riprova Diffondi" }) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const words = useMemo(() => normalizeWords(value), [value]);
  const busyRef = useRef(false);
  const closeRef = useRef(onClose);

  useEffect(() => {
    busyRef.current = busy;
  }, [busy]);

  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;
    setValue("");
    setError("");
    setBusy(false);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const closeOnEscape = (event) => {
      if (event.key === "Escape" && !busyRef.current) closeRef.current?.();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    if (words.length !== 12) {
      setError(`Servono esattamente 12 parole. Al momento ne risultano ${words.length}.`);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const identity = await recoverCurrentIdentity(words);
      clearSigningCache();
      clearDmKeyCache();
      await onRecovered?.(identity);
      onClose?.();
    } catch (err) {
      setError(err?.message || "Ripristino della chiave non riuscito.");
    } finally {
      setBusy(false);
    }
  };

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div className="fixed inset-0 z-[2147483600] flex items-end justify-center p-0 sm:items-center sm:p-4" data-testid="identity-recovery-dialog">
      <button
        type="button"
        className="absolute inset-0 bg-ink/65 backdrop-blur-sm"
        onClick={() => !busy && onClose?.()}
        aria-label="Chiudi recupero profilo"
      />
      <section
        className="relative z-10 w-full max-h-[92dvh] overflow-y-auto rounded-t-[28px] border-2 border-b-0 border-ink bg-cream p-4 shadow-[0_-8px_0_#171717] sm:w-[min(94vw,520px)] sm:rounded-[28px] sm:border-b-2 sm:shadow-[8px_8px_0_#171717]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="identity-recovery-title"
      >
        <header className="flex items-start gap-3 border-b border-edge pb-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-white">
            <KeyRound size={22} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-primary">You Voice</p>
            <h2 id="identity-recovery-title" className="text-xl font-bold text-ink">Ripristina la chiave del profilo</h2>
          </div>
          <button
            type="button"
            onClick={() => onClose?.()}
            disabled={busy}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 border-ink bg-white text-primary disabled:opacity-40"
            aria-label="Chiudi"
          >
            <X size={19} />
          </button>
        </header>

        <div className="mt-4 rounded-2xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">
          <div className="flex items-start gap-2">
            <AlertTriangle size={18} className="mt-0.5 shrink-0" />
            <p>
              Il browser conserva il profilo, ma non riesce più a decifrare la chiave privata. Inserisci le 12 parole ricevute durante la registrazione: nome, avatar e messaggi resteranno invariati.
            </p>
          </div>
        </div>

        <form className="mt-4" onSubmit={submit}>
          <label htmlFor="identity-recovery-words" className="block text-sm font-bold text-ink">
            Le tue 12 parole di recupero
          </label>
          <textarea
            id="identity-recovery-words"
            data-testid="identity-recovery-words"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            disabled={busy}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            rows={5}
            placeholder="Scrivi o incolla qui le 12 parole, separate da uno spazio"
            className="mt-2 w-full resize-none rounded-2xl border-2 border-ink bg-white px-4 py-3 text-base leading-7 outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60"
          />
          <div className="mt-2 flex items-center justify-between gap-3 text-xs">
            <span className={words.length === 12 ? "font-bold text-emerald-700" : "text-muted"}>
              {words.length}/12 parole
            </span>
            <span className="inline-flex items-center gap-1 text-muted">
              <ShieldCheck size={14} /> Restano su questo dispositivo
            </span>
          </div>

          {error && (
            <p className="mt-3 rounded-xl border border-primary/40 bg-white p-3 text-sm font-semibold text-primary" role="alert" data-testid="identity-recovery-error">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy || words.length !== 12}
            className="mt-4 flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-primary px-4 py-3 text-sm font-bold text-white disabled:opacity-40"
            data-testid="identity-recovery-submit"
          >
            <KeyRound size={18} /> {busy ? "Ripristino e verifica in corso…" : actionLabel}
          </button>
        </form>

        <p className="mt-4 text-xs leading-5 text-muted">
          Senza le 12 parole la vecchia chiave crittografica non può essere ricostruita in modo sicuro. Le parole non vengono inviate al server.
        </p>
      </section>
    </div>,
    document.body,
  );
}
